/**
 * ReferralPartnersTab - Admin tab for managing commission-based referral partners
 * 
 * Features:
 * - View all referral partners with their commission balance and stats
 * - See pending payout requests
 * - Approve and mark payouts as paid
 * - Track expenses from payouts
 * - Leaderboard of top performers
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, DollarSign, TrendingUp, Clock, Search, CheckCircle, XCircle,
  AlertCircle, Wallet, Car, Bus, Building, Filter, Download, RefreshCw,
  ArrowUpRight, Ban, Play, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const PARTNER_TYPE_ICONS = {
  taxi: Car,
  tour_bus: Bus,
  hotel: Building,
  other: Users
};

export default function ReferralPartnersTab() {
  // Data state
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [approveAmount, setApproveAmount] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPartnerDetails, setShowPartnerDetails] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, partnersRes, payoutsRes, historyRes, leaderboardRes] = await Promise.all([
        api.get('/referral-partners/admin/stats'),
        api.get('/referral-partners/admin/list'),
        api.get('/referral-partners/admin/pending-payouts'),
        api.get('/referral-partners/admin/payout-history?limit=20'),
        api.get('/referral-partners/admin/leaderboard?limit=10')
      ]);
      
      setStats(statsRes.data);
      setPartners(partnersRes.data.partners || []);
      setPendingPayouts(payoutsRes.data.pending_payouts || []);
      setPayoutHistory(historyRes.data.payout_history || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch referral partner data:', error);
      toast.error('Failed to load referral partner data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprovePayout = async () => {
    if (!selectedPayout || !approveAmount) {
      toast.error('Please enter an amount to approve');
      return;
    }
    
    const amount = parseFloat(approveAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setIsApproving(true);
    
    try {
      await api.post('/referral-partners/admin/approve-payout', {
        payout_id: selectedPayout.payout_id,
        amount_approved: amount,
        notes: approveNotes || undefined
      });
      
      toast.success(`Payout of $${amount.toFixed(2)} approved and marked as paid`);
      setSelectedPayout(null);
      setApproveAmount('');
      setApproveNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payout');
    } finally {
      setIsApproving(false);
    }
  };
  
  const handleRejectPayout = async (payoutId) => {
    if (!confirm('Are you sure you want to reject this payout request?')) return;
    
    try {
      await api.post(`/referral-partners/admin/reject-payout?payout_id=${payoutId}&reason=Request rejected by admin`);
      toast.success('Payout request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject payout');
    }
  };
  
  const handleUpdatePartnerStatus = async (contactId, newStatus) => {
    try {
      await api.put(`/referral-partners/admin/partner/${contactId}/status?status=${newStatus}`);
      toast.success(`Partner ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update partner status');
    }
  };
  
  // Filter partners
  const filteredPartners = partners.filter(p => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!p.name?.toLowerCase().includes(query) && 
          !p.phone?.includes(query) && 
          !p.code?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (typeFilter !== 'all' && p.partner_type !== typeFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="referral-partners-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Partners</p>
                <p className="text-xl font-bold">{stats?.total_partners || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Balance</p>
                <p className="text-xl font-bold">${stats?.total_commission_balance?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Earned</p>
                <p className="text-xl font-bold">${stats?.total_commission_earned?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Paid Out</p>
                <p className="text-xl font-bold">${stats?.total_commission_paid_out?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Referral Revenue</p>
                <p className="text-xl font-bold">${stats?.total_referral_revenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={pendingPayouts.length > 0 ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                pendingPayouts.length > 0 
                  ? 'bg-amber-200 dark:bg-amber-800' 
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <Clock className={`w-5 h-5 ${pendingPayouts.length > 0 ? 'text-amber-700' : 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Payouts</p>
                <p className="text-xl font-bold">{stats?.pending_payouts_count || 0}</p>
                {stats?.pending_payouts_amount > 0 && (
                  <p className="text-xs text-amber-600">${stats.pending_payouts_amount.toFixed(2)}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Pending Payouts Section */}
      {pendingPayouts.length > 0 && (
        <Card className="border-amber-300">
          <CardHeader className="bg-amber-50 dark:bg-amber-900/20">
            <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <AlertCircle className="w-5 h-5" />
              Pending Payout Requests ({pendingPayouts.length})
            </CardTitle>
            <CardDescription>Partners waiting for cash withdrawal approval</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {pendingPayouts.map((payout) => (
                <div key={payout.payout_id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">{payout.partner_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {payout.partner_phone} • {payout.partner_code}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Requested: {new Date(payout.requested_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-amber-600">
                        ${payout.amount_requested?.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">Requested</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          setSelectedPayout(payout);
                          setApproveAmount(payout.amount_requested?.toString() || '');
                        }}
                        data-testid={`approve-payout-${payout.payout_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleRejectPayout(payout.payout_id)}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Partners List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Referral Partners</CardTitle>
                  <CardDescription>Manage taxi drivers, tour operators, and hotel staff</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, phone, or code..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="partner-search"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Partner Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="taxi">Taxi</SelectItem>
                    <SelectItem value="tour_bus">Tour Bus</SelectItem>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-sm">Partner</th>
                      <th className="text-left p-3 font-medium text-sm">Code</th>
                      <th className="text-right p-3 font-medium text-sm">Balance</th>
                      <th className="text-right p-3 font-medium text-sm">Earned</th>
                      <th className="text-right p-3 font-medium text-sm">Referrals</th>
                      <th className="text-center p-3 font-medium text-sm">Status</th>
                      <th className="text-right p-3 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredPartners.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground">
                          No partners found
                        </td>
                      </tr>
                    ) : (
                      filteredPartners.map((partner) => {
                        const TypeIcon = PARTNER_TYPE_ICONS[partner.partner_type] || Users;
                        return (
                          <tr key={partner.contact_id} className="hover:bg-muted/30">
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <TypeIcon className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium">{partner.name}</p>
                                  <p className="text-xs text-muted-foreground">{partner.phone}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <code className="text-xs bg-muted px-2 py-1 rounded">{partner.code}</code>
                            </td>
                            <td className="p-3 text-right font-semibold text-green-600">
                              ${partner.commission_balance?.toFixed(2) || '0.00'}
                            </td>
                            <td className="p-3 text-right">
                              ${partner.total_commission_earned?.toFixed(2) || '0.00'}
                            </td>
                            <td className="p-3 text-right">
                              {partner.total_referral_sales || 0}
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant={partner.status === 'active' ? 'default' : 'destructive'}>
                                {partner.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex justify-end gap-1">
                                {partner.status === 'active' ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleUpdatePartnerStatus(partner.contact_id, 'suspended')}
                                    title="Suspend Partner"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => handleUpdatePartnerStatus(partner.contact_id, 'active')}
                                    title="Activate Partner"
                                  >
                                    <Play className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Sidebar - Leaderboard & Stats */}
        <div className="space-y-6">
          {/* Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-500" />
                Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {leaderboard.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  No data yet
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {leaderboard.map((partner, index) => (
                    <div key={partner.code} className="p-3 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-700' :
                        index === 2 ? 'bg-amber-100 text-amber-700' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{partner.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {partner.total_referral_sales} referrals
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">
                          ${partner.total_commission_earned?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Partner Type Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Partner Types</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.partner_type_breakdown && Object.keys(stats.partner_type_breakdown).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.partner_type_breakdown).map(([type, count]) => {
                    const Icon = PARTNER_TYPE_ICONS[type] || Users;
                    return (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="capitalize">{type.replace('_', ' ')}</span>
                        </div>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No partners yet</p>
              )}
            </CardContent>
          </Card>
          
          {/* Program Info */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Program Rates</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-amber-700 dark:text-amber-300">Customer Discount</span>
                  <span className="font-semibold">{stats?.customer_discount_rate || 5}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700 dark:text-amber-300">Partner Commission</span>
                  <span className="font-semibold">{stats?.commission_rate || 10}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700 dark:text-amber-300">Min Payout</span>
                  <span className="font-semibold">${stats?.min_payout_amount || 25}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Approve Payout Modal */}
      <Dialog open={!!selectedPayout} onOpenChange={() => setSelectedPayout(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Payout</DialogTitle>
            <DialogDescription>
              Review and approve the payout request for {selectedPayout?.partner_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Partner</p>
                  <p className="font-medium">{selectedPayout?.partner_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Code</p>
                  <p className="font-medium">{selectedPayout?.partner_code}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Requested Amount</p>
                  <p className="font-bold text-lg">${selectedPayout?.amount_requested?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Requested On</p>
                  <p className="font-medium">
                    {selectedPayout && new Date(selectedPayout.requested_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="approve-amount">Amount to Pay ($)</Label>
              <Input
                id="approve-amount"
                type="number"
                step="0.01"
                value={approveAmount}
                onChange={(e) => setApproveAmount(e.target.value)}
                data-testid="approve-amount-input"
              />
              <p className="text-xs text-muted-foreground">
                You can adjust the amount if needed
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="approve-notes">Notes (Optional)</Label>
              <Textarea
                id="approve-notes"
                placeholder="Add any notes about this payout..."
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPayout(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApprovePayout}
              disabled={isApproving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-approve-btn"
            >
              {isApproving ? 'Processing...' : `Approve & Mark Paid ($${parseFloat(approveAmount || 0).toFixed(2)})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
